import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { RotateCcwIcon, StarIcon, XIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { SwipeCard, type SwipeCardHandle, type SwipeStamps } from './SwipeCard';
import { spring } from '../../utils/motion';
import type { SwipeDirection } from '../../types/marketplace';
interface SwipeDeckProps<T> {
  items: T[];
  getKey: (item: T) => string;
  renderCard: (item: T, isTop: boolean) => React.ReactNode;
  onSwipe: (item: T, dir: SwipeDirection) => void;
  stamps: SwipeStamps;
  likeLabel: string;
  likeIcon: React.ReactNode;
  allowUp?: boolean;
  onUndo?: () => void;
  canUndo?: boolean;
  empty: React.ReactNode;
  className?: string;
}
export function SwipeDeck<T>({
  items,
  getKey,
  renderCard,
  onSwipe,
  stamps,
  likeLabel,
  likeIcon,
  allowUp,
  onUndo,
  canUndo,
  empty,
  className
}: SwipeDeckProps<T>) {
  const topRef = useRef<SwipeCardHandle>(null);
  const visible = items.slice(0, 3);
  const disabled = items.length === 0;
  return <div className={twMerge('flex h-full flex-col', className)} data-id="element-756" data-mp="1v9s58xqau1si">
      <div className="relative mx-auto w-full max-w-[420px] flex-1" style={{
      minHeight: 380
    }} data-id="element-757" data-mp="2083ep7comaa7">
        {disabled ? empty : visible.map((item, i) => <SwipeCard key={getKey(item)} ref={i === 0 ? topRef : undefined} depth={i} stamps={stamps} allowUp={allowUp} onSwipe={dir => onSwipe(item, dir)} data-id="element-758" data-mp="290doinjo0dqx" data-mpi={i}>
                  {renderCard(item, i === 0)}
                </SwipeCard>).reverse()}
      </div>

      <div className="mt-5 flex items-center justify-center gap-4" data-id="element-759" data-mp="1qbqleal1tyfz">
        <DeckButton label="Undo last pass" onClick={() => onUndo?.()} disabled={!canUndo} className="h-11 w-11 border border-line bg-surface text-ink-2" data-id="element-760" data-mp="2wrj3z1lp140p">
          <RotateCcwIcon className="h-[18px] w-[18px]" data-id="element-761" data-mp="11nbl8m6c874b" />
        </DeckButton>
        <DeckButton label="Pass" onClick={() => topRef.current?.swipe('left')} disabled={disabled} className="h-14 w-14 border border-line bg-surface text-ink" data-id="element-762" data-mp="xm9kg0wrvn08">
          <XIcon className="h-6 w-6" strokeWidth={2.5} data-id="element-763" data-mp="16n7i81gmayad" />
        </DeckButton>
        {allowUp && <DeckButton label="Save for later" onClick={() => topRef.current?.swipe('up')} disabled={disabled} className="h-12 w-12 border border-line bg-surface text-brand-strong" data-id="element-764" data-mp="ix3m0f5re0te">
            <StarIcon className="h-5 w-5" strokeWidth={2.5} data-id="element-765" data-mp="sny9fmuqr6gf" />
          </DeckButton>}
        <DeckButton label={likeLabel} onClick={() => topRef.current?.swipe('right')} disabled={disabled} className="h-16 w-16 bg-accent text-on-accent shadow-[0_8px_24px_rgb(0_0_0/0.15)]" data-id="element-766" data-mp="nvoefserrg7a">
          {likeIcon}
        </DeckButton>
      </div>
    </div>;
}
interface DeckButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}
function DeckButton({
  label,
  onClick,
  disabled,
  className,
  children
}: DeckButtonProps) {
  return <motion.button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} whileHover={{
    scale: 1.06
  }} whileTap={{
    scale: 0.88
  }} transition={spring.snappy} className={twMerge('grid place-items-center rounded-full transition-opacity duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-40', className)} data-id="element-767" data-mp="21w1bqgx0k0aa">
      {children}
    </motion.button>;
}